import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldAlert, Gavel, Sparkles, AlertTriangle, ArrowRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/ui-helpers";
import { toast } from "sonner";

const label = (s) => (s || "").replace(/_/g, " ");
const TABS = [["disputes", "Disputes", Gavel], ["ai", "AI Review Flags", Sparkles], ["compliance", "Compliance Alerts", AlertTriangle]];

export default function AdminModeration() {
  const nav = useNavigate();
  const [tab, setTab] = useState("disputes");
  const [disputes, setDisputes] = useState({ disputes: [], open: 0, count: 0 });
  const [flags, setFlags] = useState({ flags: [], count: 0 });
  const [comp, setComp] = useState(null);

  const load = useCallback(() => {
    api.get("/marketplace/admin/disputes").then((r) => setDisputes(r.data)).catch(() => {});
    api.get("/reviews/admin/ai/flags").then((r) => setFlags(r.data)).catch(() => {});
    api.get("/marketplace/admin/compliance/alerts").then((r) => setComp(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const resolve = async (d) => {
    const decision = window.prompt(`Resolve dispute on ${d.number}. Decision (e.g. refund / replacement / rejected):`);
    if (!decision) return;
    const reason = window.prompt("Resolution note (audited, shared with customer):");
    if (!reason) return;
    try { await api.post(`/marketplace/admin/orders/${d.suborder_id}/dispute-resolve`, { decision, reason }); toast.success("Dispute resolved"); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-moderation">
      <PageHeader title="Moderation & Compliance" icon={ShieldAlert} subtitle="One place to resolve disputes, review AI-flagged reviews, and act on compliance alerts. All figures are real." />

      <div className="mb-4 grid grid-cols-3 gap-3">
        <button onClick={() => setTab("disputes")} data-testid="mod-card-disputes" className="rounded-2xl border border-slate-200 bg-white p-4 text-left transition hover:shadow-md">
          <p className="text-2xl font-extrabold text-slate-900">{disputes.open}</p><p className="text-xs text-slate-500">Open disputes</p></button>
        <button onClick={() => setTab("ai")} data-testid="mod-card-ai" className="rounded-2xl border border-slate-200 bg-white p-4 text-left transition hover:shadow-md">
          <p className="text-2xl font-extrabold text-slate-900">{flags.count}</p><p className="text-xs text-slate-500">AI-flagged reviews</p></button>
        <button onClick={() => setTab("compliance")} data-testid="mod-card-compliance" className="rounded-2xl border border-slate-200 bg-white p-4 text-left transition hover:shadow-md">
          <p className="text-2xl font-extrabold text-slate-900">{comp?.total ?? 0}</p><p className="text-xs text-slate-500">Compliance alerts</p></button>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {TABS.map(([k, lbl, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`mod-tab-${k}`}
            className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold ${tab === k ? "brand-gradient-bg text-white" : "bg-white text-slate-600 ring-1 ring-slate-200"}`}>
            <Icon className="h-3.5 w-3.5" /> {lbl}</button>
        ))}
      </div>

      {tab === "disputes" && (
        <div className="space-y-3" data-testid="mod-disputes">
          {disputes.disputes.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center"><Gavel className="mx-auto h-8 w-8 text-slate-300" /><p className="mt-3 font-semibold text-slate-700">No disputes</p></div>
          ) : disputes.disputes.map((d) => (
            <div key={d.suborder_id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`dispute-${d.suborder_id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="text-sm font-bold text-slate-800">{d.number} <span className="font-normal text-slate-400">· {d.customer_name} vs {d.seller_name}</span></p>
                  <p className="text-xs text-slate-500">{d.items?.map((it) => `${it.name}×${it.qty}`).join(", ")} · ₹{d.subtotal}</p>
                  <p className="mt-1 text-xs text-rose-600">Reason: {d.dispute?.reason}</p>
                  {d.dispute?.status === "resolved" && <p className="text-xs text-emerald-600">Resolved: {d.dispute?.decision} — {d.dispute?.resolved_reason}</p>}
                </div>
                <div className="flex items-center gap-2">
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${d.dispute?.status === "open" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>{label(d.dispute?.status)}</span>
                  {d.dispute?.status === "open" && <button onClick={() => resolve(d)} data-testid={`resolve-${d.suborder_id}`} className="rounded-full brand-gradient-bg px-3 py-1.5 text-xs font-semibold text-white">Resolve</button>}
                  <button onClick={() => nav("/admin/orders")} className="rounded-full border border-slate-200 px-3 py-1.5 text-xs text-slate-600">View order</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "ai" && (
        <div className="space-y-3" data-testid="mod-ai">
          <div className="flex items-center justify-between rounded-xl border border-indigo-100 bg-indigo-50 p-3 text-xs text-indigo-700">
            <span>AI is advisory only — it never hides or removes reviews automatically. Moderate in Reviews & Ratings.</span>
            <button onClick={() => nav("/admin/reviews")} data-testid="mod-goto-reviews" className="inline-flex items-center gap-1 font-semibold underline">Open Reviews <ArrowRight className="h-3 w-3" /></button>
          </div>
          {flags.flags.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center"><Sparkles className="mx-auto h-8 w-8 text-slate-300" /><p className="mt-3 font-semibold text-slate-700">No AI-flagged reviews</p></div>
          ) : flags.flags.map((f) => (
            <div key={f.review_id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`aiflag-${f.review_id}`}>
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-slate-800">{"★".repeat(f.rating || 0)}<span className="ml-2 text-slate-500">{f.title || "(no title)"}</span></p>
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${f.status === "published" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{label(f.status)}</span>
              </div>
              <p className="mt-1 line-clamp-2 text-xs text-slate-500">{f.body}</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {f.ai?.priority && <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-700">priority: {f.ai.priority}</span>}
                {["spam", "abusive", "likely_fake", "policy_violation"].filter((k) => f.ai?.[k]).map((k) => <span key={k} className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">{k.replace(/_/g, " ")}</span>)}
                {f.reported_count > 0 && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">{f.reported_count} report(s)</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "compliance" && (
        <div className="space-y-4" data-testid="mod-compliance">
          {!comp ? null : (
            <>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {[["Reconciliation required", comp.reconciliation_required, "/admin/settlements"],
                  ["Reported reviews", comp.reported_reviews, "/admin/reviews"],
                  ["Open disputes", comp.open_disputes, null]].map(([lbl, v, to]) => (
                  <button key={lbl} onClick={() => to && nav(to)} className="rounded-2xl border border-slate-200 bg-white p-4 text-left transition hover:shadow-md" data-testid={`comp-${lbl.toLowerCase().replace(/\s+/g, "-")}`}>
                    <p className="text-2xl font-extrabold text-slate-900">{v}</p><p className="text-xs text-slate-500">{lbl}</p></button>
                ))}
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="comp-verification">
                <div className="mb-2 flex items-center justify-between"><p className="text-sm font-semibold text-slate-800">Seller verification needing action</p>
                  <button onClick={() => nav("/admin/seller-verification")} className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 underline">Open <ArrowRight className="h-3 w-3" /></button></div>
                {comp.seller_verification.length === 0 ? <p className="py-3 text-center text-sm text-slate-400">All sellers up to date.</p>
                  : comp.seller_verification.map((v) => (
                    <div key={v.seller_id} className="flex items-center justify-between border-t border-slate-50 py-2 first:border-t-0">
                      <span className="text-sm text-slate-700">{v.seller_name}</span>
                      <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700 capitalize">{label(v.status)}</span>
                    </div>))}
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="comp-bank">
                <p className="mb-2 text-sm font-semibold text-slate-800">Bank verification issues</p>
                {comp.bank_issues.length === 0 ? <p className="py-3 text-center text-sm text-slate-400">No bank issues.</p>
                  : comp.bank_issues.map((b) => (
                    <div key={b.seller_id} className="flex items-center justify-between border-t border-slate-50 py-2 first:border-t-0">
                      <span className="text-sm text-slate-700">{b.seller_name} · {b.account_masked}</span>
                      <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-700 capitalize">{label(b.status)}</span>
                    </div>))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
